import IBundleConfig from "gpx-games-config";
import { GpxWebGLRenderingContext } from "./sdk";

const webGLContextTypes: {
	[type: number]: {
		type: string,
		description: string,
		majorVersion: number
	}
} = {
	2: {
		type: "webgl2",
		description: "WebGL 2.0",
		majorVersion: 2,
	},
	1: {
		type: "webgl",
		description: "WebGL 1.0",
		majorVersion: 1

	},
	0: {
		type: "experimental-webgl",
		description: "WebGL 1.0 FALLBACK",
		majorVersion: 1
	}
}

const factoryVersion = 3;
const prefix = `webgl-factory.v${factoryVersion}: `;
const resolveTimeout = 150;
// eslint-disable-next-line
const webGlContextAttributes: any = {
	alpha: false,
	antialias: false,
	depth: true,
	explicitSwapControl: 0,
	failIfMajorPerformanceCaveat: false,
	majorVersion: 1,
	minorVersion: 0,
	powerPreference: "high-performance",
	preferLowPowerToHighPerformance: false,
	premultipliedAlpha: false,
	preserveDrawingBuffer: false,
	proxyContextToMainThread: 0,
	renderViaOffscreenBackBuffer: 0,
	stencil: true,
	maxWebGLVersion: 2,
};

export function GetWebGLContext(canvas: HTMLCanvasElement, config: IBundleConfig, maxWebGLVersion?: number): Promise<GpxWebGLRenderingContext> {
	if (config.webglAttributes !== undefined) {
		for (const next of Object.keys(config.webglAttributes)) {
			webGlContextAttributes[next] = config.webglAttributes[next];
		}
	}
	if (typeof maxWebGLVersion === "number") {
		webGlContextAttributes.maxWebGLVersion = maxWebGLVersion;
	}

	let resolveTimeoutId = 0;
	let resolveFn: (GL: GpxWebGLRenderingContext) => void = () => { /**/ };

	function tryToResolveContext() {
		clearTimeout(resolveTimeoutId);

		const gpxContext = createWebGLContext(canvas);
		if (typeof gpxContext !== "undefined" && !gpxContext.context.isContextLost()) {
			// eslint-disable-next-line
			resolveTimeoutId = setTimeout(() => resolveFn(gpxContext), resolveTimeout) as any;
		}
	}

	// eslint-disable-next-line
	function onContextCreationError(e: Event | any) {
		clearTimeout(resolveTimeoutId);

		// eslint-disable-next-line
		console.error(prefix + "webglcontextcreateionerror: " + e.statusMessage || "?", e);
	}

	// eslint-disable-next-line
	function onContextLost(e: Event | any) {
		clearTimeout(resolveTimeoutId);
		e.preventDefault();

		// eslint-disable-next-line
		console.error(prefix + "webglcontextlost: " + e.statusMessage || "?", e);
	}

	function onContextRestored() {
		tryToResolveContext();

		// eslint-disable-next-line
		console.log(prefix + "webglcontextrestored");
	}

	canvas.addEventListener("webglcontextcreationerror", onContextCreationError, false);
	canvas.addEventListener("webglcontextlost", onContextLost, false);
	canvas.addEventListener("webglcontextrestored", onContextRestored, false);

	return new Promise<GpxWebGLRenderingContext>((resolve) => {
		resolveFn = resolve;
		tryToResolveContext();
	}).then((gpxContext) => {
		patchGetContextFn(canvas, gpxContext);

		canvas.removeEventListener("webglcontextcreationerror", onContextCreationError, false);
		canvas.removeEventListener("webglcontextlost", onContextLost, false);
		canvas.removeEventListener("webglcontextrestored", onContextRestored, false);
		return gpxContext;
	});
}

function createWebGLContext(canvas: HTMLCanvasElement): GpxWebGLRenderingContext | undefined {
	for (let webGLVersion = webGlContextAttributes.maxWebGLVersion; webGLVersion >= 0; --webGLVersion) {
		const contextType = webGLContextTypes[webGLVersion];
		if (typeof contextType === "undefined") {
			// eslint-disable-next-line
			console.error(prefix + "Unable to find description for context: " + contextType + "\nAttributes:\n" + JSON.stringify(webGlContextAttributes, null, 2));
			continue;
		}
		const ctx = canvas.getContext(contextType.type, webGlContextAttributes);
		if (ctx !== null && ctx !== undefined) {
			webGlContextAttributes.majorVersion = contextType.majorVersion;
			// eslint-disable-next-line
			console.log(prefix + contextType.description + " context created\nAttributes:\n" + JSON.stringify(webGlContextAttributes, null, 2));
			return {
				factoryVersion,
				context: ctx as WebGLRenderingContext,
				version: contextType.majorVersion
			};
		}

		// eslint-disable-next-line
		console.error(prefix + "Unable to create context: " + contextType.description);
	}
	// eslint-disable-next-line
	console.error("Attributes:\n" + JSON.stringify(webGlContextAttributes, null, 2));
	return undefined;
}

function patchGetContextFn(canvas: HTMLCanvasElement, gpxContext: GpxWebGLRenderingContext) {
	canvas.getContext = (ctxType: string) => {
		for (const contextType of Object.values(webGLContextTypes)) {
			if (contextType.type === ctxType) {
				if (contextType.majorVersion === gpxContext.version) {
					// eslint-disable-next-line
					console.log(prefix + "Get WebGL context: " + ctxType + ". Force use pre-created WebGL context");
					// eslint-disable-next-line
					return gpxContext.context as any;
				}
				break;
			}
		}
		// eslint-disable-next-line
		console.error(prefix + "Unable to get context: " + ctxType + "\nAttributes:\n" + JSON.stringify(webGlContextAttributes, null, 2));
		return null;
	};
}
